import { createAction, handleActions, type Action } from 'redux-actions';
import type { Dispatch } from 'react';
import _ from 'lodash';
import type {
  CategoryCustomizationKeys,
  ReferralStrategyForEdit,
  UnderwriteQuoteState,
} from '../types';
import type {
  GlobalConfig,
  RateModifier,
  RaterXCoverage,
  RaterXPreset,
} from '../../../api/types/rater-x-preset';
import type { CoverageState, RaterOptionKeysType } from '../../../types';
import type { CarrierEligibility } from '../../../../../../../api/quotes.api';

const INITIAL_STATE = {};

/* ACTION TYPES */
const EFFECTIVE_DATE_ENTERED = 'EFFECTIVE_DATE_ENTERED';
const END_DATE_ENTERED = 'END_DATE_ENTERED';
const CART_COUNT_UPDATED = 'CART_COUNT_UPDATED';
const GLOBAL_INPUT_UPDATED = 'GLOBAL_INPUT_UPDATED';
const COVERAGE_UPDATED = 'COVERAGE_UPDATED';
export const RATER_X_RECALCULATED = 'RATER_X_RECALCULATED';
const RATE_MODIFIER_UPDATED = 'RATE_MODIFIER_UPDATED';
const RATER_X_ENABLED_RESET = 'RATER_X_ENABLED_RESET';
const RATER_OPTION_UPDATED = 'RATER_OPTION_UPDATED';
const CARRIER_OPTIONS_UPDATED = 'CARRIER_OPTIONS_UPDATED';
const CATEGORY_CUSTOMIZED = 'CATEGORY_CUSTOMIZED';
const COMPONENT_GLOBAL_UPDATED = 'COMPONENT_GLOBAL_UPDATED';
const BULK_COVERAGE_UPDATED = 'BULK_COVERAGE_UPDATED';
const REPAIR_RATER_REQUESTED = 'REPAIR_RATER_REQUESTED';

export const createActionHandlers = (
  dispatch: Dispatch<Action<UnderwriteQuoteState>>
) => {
  return {
    setEffectiveDate: (date: string) =>
      dispatch(createAction(EFFECTIVE_DATE_ENTERED)(date)),

    setEndDate: (date: string) =>
      dispatch(createAction(END_DATE_ENTERED)(date)),

    setCartCount: (count: number) =>
      dispatch(createAction(CART_COUNT_UPDATED)(count)),

    setGlobalValue: (payload: GlobalInputPayload) =>
      dispatch(createAction(GLOBAL_INPUT_UPDATED)(payload)),

    setCoverage: (payload: UpdateCoveragePayload) =>
      dispatch(createAction(COVERAGE_UPDATED)(payload)),

    setRaterXRecalculated: (payload: RaterXRecalculatedPayload) =>
      dispatch(createAction(RATER_X_RECALCULATED)(payload)),

    setRatingOption: (payload: RateModifierPayload | RateModifierPayload[]) =>
      dispatch(createAction(RATE_MODIFIER_UPDATED)(payload)),

    resetRaterXEnabled: (payload: boolean) =>
      dispatch(createAction(RATER_X_ENABLED_RESET)(payload)),

    setRaterOption: (payload: RaterOptionPayload) =>
      dispatch(createAction(RATER_OPTION_UPDATED)(payload)),

    setCarrierOptions: (options: CarrierEligibility[]) =>
      dispatch(createAction(CARRIER_OPTIONS_UPDATED)(options)),

    setCategoryCustomization: (payload: CategoryCustomizationPayload) =>
      dispatch(createAction(CATEGORY_CUSTOMIZED)(payload)),

    setComponentGlobal: (key: string) => (payload: GlobalInputPayload) =>
      dispatch(
        createAction(COMPONENT_GLOBAL_UPDATED)({
          key,
          ...payload,
        })
      ),

    setBulkCoverages: (payload: BulkUpdateCoveragePayload) =>
      dispatch(createAction(BULK_COVERAGE_UPDATED)(payload)),

    onRepairRaterRequested: () =>
      dispatch(createAction(REPAIR_RATER_REQUESTED)()),
  };
};

export function primeXUnderwritingReducer<
  T extends Partial<UnderwriteQuoteState>
>() {
  return handleActions<T, any>(
    {
      [EFFECTIVE_DATE_ENTERED]: (state, action: Action<string>) => ({
        ...state,
        effectiveDate: action.payload,
        enableRaterXRecalculate: true,
        globalChanged: true,
      }),

      [END_DATE_ENTERED]: (state, action: Action<string>) => ({
        ...state,
        endDate: action.payload,
        enableRaterXRecalculate: true,
      }),

      [GLOBAL_INPUT_UPDATED]: (state, action: Action<GlobalInputPayload>) => {
        const existingGlobalProps = state.global?.value?.props;

        return {
          ...state,
          enableRaterXRecalculate: true,
          globalChanged: true,
          global: {
            ...state.global,
            value: {
              ...state.global?.value,
              props: {
                ...existingGlobalProps,
                ...action.payload,
              },
            },
          },
        };
      },

      [COMPONENT_GLOBAL_UPDATED]: (
        state,
        action: Action<GlobalInputPayload & { key: string }>
      ) => {
        const { key, ...payload } = action.payload;
        const valToLock = Object.keys(payload);

        return {
          ...state,
          enableRaterXRecalculate: true,
          globalChanged: true,
          componentGlobals: {
            ...state.componentGlobals,
            [key]: {
              ...state.componentGlobals?.[key],
              lockValues: [
                ...(state.componentGlobals?.[key]?.lockValues ?? []),
                ...valToLock,
              ],
              value: {
                props: {
                  ...state.componentGlobals?.[key]?.value?.props,
                  ...payload,
                },
              },
            },
          },
        };
      },

      [COVERAGE_UPDATED]: (state, action: Action<UpdateCoveragePayload>) => {
        return {
          ...state,
          enableRaterXRecalculate: true,
          coverageState: {
            ...state.coverageState,
            [action.payload.id]: {
              ...state.coverageState?.[action.payload.id],
              isDirty: true,
              isPegValue: action.payload.isPegValue ?? true,
              isSelected:
                action.payload.isSelected ??
                state.coverageState?.[action.payload.id]?.isSelected ??
                false,
              ...(action.payload.referralStrategyForEdit !== undefined && {
                referralStrategyForEdit: action.payload.referralStrategyForEdit,
              }),
              ...(action.payload.value && {
                value: {
                  props: {
                    ...state.coverageState?.[action.payload.id].value?.props,
                    ...(action.payload.value?.props &&
                      action.payload.value.props),
                  },
                },
              }),
            },
          },
        };
      },

      [BULK_COVERAGE_UPDATED]: (
        state,
        action: Action<BulkUpdateCoveragePayload>
      ) => {
        const newCoverageState = { ...state.coverageState };

        for (const update of action.payload.updates) {
          const prev: Partial<CoverageState[string]> =
            newCoverageState[update.id] || {};

          newCoverageState[update.id] = {
            ...prev,
            isDirty: true,
            isPegValue: true,
            isSelected: update.isSelected ?? prev.isSelected,
            ...(update.referralStrategyForEdit !== undefined && {
              referralStrategyForEdit: update.referralStrategyForEdit,
            }),
            ...(update.value && {
              value: {
                props: {
                  ...prev.value?.props,
                  ...(update.value.props ?? {}),
                },
              },
            }),
          } as CoverageState[string];
        }

        return {
          ...state,
          enableRaterXRecalculate: true,
          coverageState: newCoverageState,
        };
      },

      [RATER_X_RECALCULATED]: (
        state,
        action: Action<RaterXRecalculatedPayload>
      ) => {
        const coverageState = Object.values(state.coverageState || {}).reduce(
          hydrateRaterResponseWithLockValue,
          action.payload.coverages
        );

        const ratingOptionState = Object.values(
          state.ratingOptions || {}
        ).reduce(
          hydrateRaterResponseWithLockValue,
          action.payload.ratingOptions
        );

        const componentGlobals = Object.entries(
          state.componentGlobals || {}
        ).reduce((acc, [key, value]) => {
          return {
            ...acc,
            [key]: {
              ...value,
              ...action.payload.componentGlobals?.[key],
            },
          };
        }, {});

        return {
          ...state,
          raterOptions: {
            ...state.raterOptions,
            ...(action.payload.treatyId && {
              carrier: {
                ...state.raterOptions.carrier,
                value: action.payload.treatyId,
              },
            }),
          },
          coverageState: _.isEmpty(action.payload.coverages)
            ? {}
            : coverageState,
          global: action.payload.global,
          enableRaterXRecalculate: false,
          globalChanged: false,
          ...(action.payload.carrierPrefix && {
            carrierPrefix: action.payload.carrierPrefix,
          }),
          ...(action.payload.treatyId && {
            treatyId: action.payload.treatyId,
          }),
          ratingOptions: _.isEmpty(action.payload.ratingOptions)
            ? {}
            : ratingOptionState,
          totalPremium: action.payload.totalPremium,
          ...(action.payload.componentGlobals && {
            componentGlobals,
          }),
          ...(action.payload.endDate && {
            endDate: action.payload.endDate,
          }),
          ...(action.payload.effectiveDate && {
            effectiveDate: action.payload.effectiveDate,
          }),
        };
      },

      [RATE_MODIFIER_UPDATED]: (state, action: RateModifierPayloadAction) => {
        const modifiers = Array.isArray(action.payload)
          ? action.payload
          : [action.payload];

        const _aggregatedPayload = modifiers.reduce((acc, mod) => {
          return {
            ...acc,
            [mod.id]: {
              ...state.ratingOptions?.[mod.id],
              ...mod,
              isDirty: true,
              value: {
                props: {
                  ...(mod.value?.props?.option?.value == null &&
                    state.ratingOptions?.[mod.id].value?.props),
                  ...(mod.value?.props && mod.value.props),
                },
              },
            },
          };
        }, {});

        return {
          ...state,
          enableRaterXRecalculate: true,
          ratingOptions: {
            ...state.ratingOptions,
            ..._aggregatedPayload,
          },
        };
      },

      [RATER_X_ENABLED_RESET]: (state, action: Action<boolean>) => {
        return {
          ...state,
          enableRaterXRecalculate: action.payload,
        };
      },

      [RATER_OPTION_UPDATED]: (state, action: Action<RaterOptionPayload>) => {
        const carrierOpt = state.raterOptions.carrier.options.find(
          (c) => c.treatyId === action.payload.value
        );

        return {
          ...state,
          ...(carrierOpt != null && {
            carrierPrefix: carrierOpt.policyPrefix,
            treatyId: carrierOpt.treatyId,
          }),
          raterOptions: {
            ...state.raterOptions,
            [action.payload.id]: {
              ...state.raterOptions?.[action.payload.id],
              value: action.payload.value,
            },
          },
        };
      },

      [CARRIER_OPTIONS_UPDATED]: (
        state,
        action: Action<CarrierEligibility[]>
      ) => {
        return {
          ...state,
          raterOptions: {
            ...state.raterOptions,
            carrier: {
              ...state.raterOptions.carrier,
              options: action.payload,
            },
          },
        };
      },

      [CATEGORY_CUSTOMIZED]: (
        state,
        action: Action<CategoryCustomizationPayload>
      ) => {
        return {
          ...state,
          enableRaterXRecalculate: true,
          categoryCustomizations: {
            [action.payload.type]: {
              ...state.categoryCustomizations?.[action.payload.type],
              ...action.payload.payload,
            },
          },
        };
      },

      [REPAIR_RATER_REQUESTED]: (state) => {
        return {
          ...state,
          enableRaterXRecalculate: true,
          globalChanged: true,
        };
      },
    },
    INITIAL_STATE as T
  );
}

type GlobalInputPayload = Partial<RaterXPreset['global']['value']['props']>;

type UpdateCoveragePayload = Partial<RaterXCoverage> & { id: string };

export type RaterXRecalculatedPayload = {
  coverages: UnderwriteQuoteState['coverageState'];
  global: RaterXPreset['global'];
  ratingOptions: UnderwriteQuoteState['ratingOptions'];
  totalPremium: number;
  componentGlobals?: Record<string, GlobalConfig>;
  endDate?: string;
  effectiveDate?: string;
  carrierPrefix?: string;
  treatyId?: string;
};

export type RateModifierPayload = Partial<RateModifier> & {
  id: string;
};

type RateModifierPayloadAction =
  | Action<RateModifierPayload>
  | Action<RateModifierPayload[]>;

type RaterOptionPayload = {
  id: RaterOptionKeysType;
  value: string;
};

type CategoryCustomizationPayload = {
  type: CategoryCustomizationKeys;
  payload: Record<string, { select: boolean }>;
};

type BulkUpdateCoveragePayload = {
  updates: UpdateCoveragePayload[];
};

function hydrateRaterResponseWithLockValue(
  acc: Record<string, any>,
  option: {
    key: string;
    isPegValue?: boolean;
    isDirty?: boolean;
    referralStrategyForEdit?: ReferralStrategyForEdit | null;
  }
) {
  const existingOption = acc?.[option.key];
  const shouldPreserveExistingValue = existingOption?.isPegValue === true;

  return {
    ...acc,
    [option.key]: {
      ...acc[option.key],
      isPegValue: acc.isPegValue,
      ...(shouldPreserveExistingValue && {
        ...existingOption,
        value: existingOption.value,

        isDirty: existingOption.isDirty,
        isSelected: existingOption.isSelected,
        referralStrategyForEdit: existingOption.referralStrategyForEdit,
      }),
      ...(option.isPegValue === true && {
        isDirty: option.isDirty,
      }),
      ...(option.referralStrategyForEdit !== undefined && {
        referralStrategyForEdit: option.referralStrategyForEdit,
      }),
    },
  };
}
